/**
 * The page-break plan of a flowing text box (SP-146): the SP-137 spike's model C.
 *
 * Text is one Lexical editor. A layout pass measures each break unit in its
 * natural flow (no breaks applied) and this module decides which units start a
 * later page, and how far each must be pushed down (a `margin-top`) to land on
 * that page's top margin. It is pure: all values are layout px relative to the
 * editor root, which sits at the top of the box.
 */
import { pagePitch } from '../model/page-layout';
import { pageDimensions } from '../model/paper';
import type { PageSetup } from '../model/paper';
import type { FlowOverride } from '../model/types';
import { clampFlowOverride } from '../model/flow-override';
import type { OverflowPage, PaperErrorReason } from '../model/layout-facts';

/** Padding of a text box, on every side. A continuation box's top edge sits
 *  exactly on the page's top margin, so its first unit lands one padding below. */
export const TEXT_BOX_PADDING = 12;

/**
 * The most pages one text box may reach, its own included, so a plan has at most `MAX_FLOW_PAGES − 1` breaks.
 * A stored `flowBreaks` and the auto pages a document gains are bounded by the same number, so a crafted or
 * corrupt value (a `y` of 1e12, say) can't make the editor lay out or append pages without end.
 */
export const MAX_FLOW_PAGES = 500;

/**
 * The largest unit index a stored `flowBreaks` may hold. It bounds what the parser accepts and what `applyTextFlow`
 * writes, so a break can never point past any text a person could plausibly write.
 */
export const MAX_FLOW_UNITS = 100_000;

/** The fewest lines a split leaves on either side of the cut (orphans and widows, SP-153 ruling 1). */
export const MIN_SPLIT_LINES = 2;

/** A unit in the natural flow, root-relative layout px. `marginBottom` is the unit's own. A unit splits
 *  line by line at a page break only when `splittable` is true (SP-153). */
export type MeasuredUnit = { top: number; bottom: number; marginBottom: number; splittable?: boolean };

/** A line in the natural flow, root-relative; `offset` is the char offset of its first character in its unit.
 *  A unit's lines are usable for a split only when line 0's offset is 0, later offsets are integers ≥ 1 and
 *  strictly increasing, and tops strictly increase. */
export type MeasuredLine = { top: number; bottom: number; offset: number };

/** Where a page's part of the box sits horizontally: `dx` from the first box's left edge, and its `width`.
 *  A `width` of NaN asks for the box's own width when that isn't known (a frame without `boxWidth`). */
export type PageGeometry = { dx: number; width: number };

/**
 * A float band in a split unit (SP-153): the gap that pushes the unit's lines from `top` on down to the next
 * page's landing. `top` is in the unit's float coordinates: the line's natural top relative to the unit's top,
 * plus the heights of that unit's earlier bands. `side`/`inset` narrow the lines after this band (until the
 * unit's next band); a plain split has `side` null and `inset` 0.
 */
export type FlowBand = { unit: number; top: number; height: number; side: 'left' | 'right' | null; inset: number };

/**
 * What `planFlow` asks the layout for beyond the units it was given (SP-153/SP-154). Unit indices are relative to
 * the measurer (0 = the first unit it measured).
 * - `lines(unit)`: the unit's lines.
 * - `remeasure(from, geometry)`: units `from…end` laid out again at another page geometry, in the natural flow,
 *   indexed from 0 = unit `from`, and the measurer for those measurements.
 * - `remeasureSplit(unit, bands, tail)`: the layout again with unit `unit`'s band rule live (`bands` are all of its
 *   bands so far, the last one narrowing). Returns units `unit…end`, indexed from 0 = `unit`, whose positions include
 *   the band gaps; the units after `unit` are laid out at `tail`. The returned measurer's `lines(0)` gives all of the
 *   unit's lines, the tail re-wrapped, with their offsets.
 */
export type FlowMeasurer = {
    lines(unit: number): MeasuredLine[];
    remeasure(from: number, geometry: PageGeometry): { units: MeasuredUnit[]; measurer: FlowMeasurer };
    remeasureSplit(
        unit: number,
        bands: FlowBand[],
        tail: PageGeometry,
    ): { units: MeasuredUnit[]; measurer: FlowMeasurer };
};

/** How a split may join a head laid out at `Gh` to a tail on a page of geometry `Gt` (ruling 3). */
export type SplitKind = 'plain' | { side: 'left' | 'right'; inset: number } | 'other';

/**
 * Edges this close count as the same (`classifySplit`, both edges alike): a west resize keeps the right edge as
 * `dx + (right − dx)`, which can round off by an ulp.
 */
export const EDGE_EPSILON = 1e-6;

/**
 * Ruling 3: `plain` when the geometries are equal (a NaN width, unknown, equals only another); a narrowing on the
 * right (`inset = w_h − w_t`) when the tail is narrower and keeps the left edge; on the left (`inset = dx_t − dx_h`)
 * when it is narrower and keeps the right edge; `other` for anything else (narrower on both sides, wider, shifted
 * at the same width, or an unknown width against a known one).
 */
export function classifySplit(head: PageGeometry, tail: PageGeometry): SplitKind {
    const same = (a: number, b: number) => a === b || (Number.isNaN(a) && Number.isNaN(b));
    if (same(head.dx, tail.dx) && same(head.width, tail.width)) return 'plain';
    if (![head.dx, head.width, tail.dx, tail.width].every(Number.isFinite) || tail.width >= head.width) return 'other';
    if (Math.abs(tail.dx - head.dx) < EDGE_EPSILON) return { side: 'right', inset: head.width - tail.width };
    if (Math.abs(tail.dx + tail.width - (head.dx + head.width)) < EDGE_EPSILON) {
        return { side: 'left', inset: tail.dx - head.dx };
    }
    return 'other';
}

/** Whether measured lines can be split on (see `MeasuredLine`). */
function usableLines(lines: readonly MeasuredLine[]): boolean {
    if (lines.length < 2) return false;
    return lines.every((line, j) => {
        if (!Number.isFinite(line.top) || !Number.isFinite(line.bottom) || !Number.isInteger(line.offset)) return false;
        if (j === 0) return line.offset === 0;
        const prev = lines[j - 1];
        return line.offset >= 1 && line.offset > prev.offset && line.top > prev.top;
    });
}

/** Where the box sits: `y` is the root's top on its own page, and `setup` is the document's paper and margins.
 *  `boxWidth` and `overrides` (the box's width and its continuations' overrides) feed free continuations (SP-154). */
export type FlowFrame = {
    y: number;
    setup: PageSetup;
    boxWidth?: number;
    overrides?: ReadonlyArray<FlowOverride | null>;
};

/** The part of the box drawn on one page, root-relative. `width` null is the first box's width. */
export type FlowSegment = { top: number; height: number; left: number; width: number | null };

export type FlowPlan = {
    /** Pages (offsets from the box's own) where a unit that starts one passes the paper's bottom edge. Always `[]` until the paper-edge check fills it (SP-141). */
    overflowPages: OverflowPage[];
    /** The index of the first unit on each later page. Non-decreasing; a repeat marks a page a too-tall unit covers. */
    breaks: number[];
    /** The `margin-top` that pushes the first unit of each later page down (skipped pages add no entry). */
    margins: Array<{ unit: number; px: number }>;
    /** One per page the box reaches, starting with its own. `null` for a page covered by a too-tall unit. */
    segments: Array<FlowSegment | null>;
    /** The first page's segment height: the box's height on its own page. */
    height: number;
    /** A unit that starts a page is taller than the page. */
    overflow: boolean;
    /** One entry per unit on a later page whose geometry differs from the first page's (for the CSS). */
    geometry: Array<{ unit: number; dx: number; width: number }>;
    /** Parallel to `breaks`: the char offset in the break's unit where a line split cuts it, or null for a
     *  whole-unit break. */
    offsets: Array<number | null>;
    /** The float bands of split units, in order (a unit's bands in page order). */
    bands: FlowBand[];
    /** For each split unit, its total height in float coordinates: its natural height (its tail re-wrapped, if
     *  narrowed) plus its bands. */
    unitHeights: Array<{ unit: number; height: number }>;
};

/**
 * Places the page breaks.
 *
 * With `k` the page offset from the box's own page, `pitch` the distance between
 * page tops, `H` the page height and `mt`/`mb` the top and bottom margins:
 * - landing(k) = k·pitch + mt − y + PAD: where the first unit of page k's border box starts;
 * - limit(k)   = k·pitch + H − mb − y − PAD: a unit fits on page k when its bottom is at or above this;
 * - paper(k)   = k·pitch + H − y: page k's paper edge.
 *
 * The first unit of a page never breaks, so a unit taller than a page stays where
 * it is (and `overflow` is set), and a box that starts below the margin still shows its first unit.
 *
 * A unit that spills past the page it lands on covers the pages it reaches. The next unit is
 * pushed past them, and each skipped page gets a repeated break (an empty slice) and a `null` segment.
 *
 * Line splits (SP-153): with a measurer, a `splittable` unit that doesn't fit splits after its last line that
 * fits, by the orphan/widow rule (at least `MIN_SPLIT_LINES` lines each side; a page's first unit keeps at least
 * one line and may ignore the widow rule; one whose lines all fit doesn't split). Its break carries the cut line's
 * char offset, and a band pushes the cut line down to the next page's landing; the unit may split again there.
 * A split never skips a page. How the next page's geometry joins the unit's (`classifySplit`, ruling 3):
 * - plain: bands only;
 * - narrower, keeping one edge: a narrowing split. The band narrows the tail on that side and the unit is measured
 *   again (`remeasureSplit`) to re-wrap it; the units after it are measured at the tail's geometry. All of a unit's
 *   narrowing is on one side: a later split that needs the other side is treated as `other`;
 * - other: the unit moves whole, or, first on its page, makes a fallback split: its tail keeps the head's geometry
 *   on the next page, whose segment covers both, and the units after it are measured at that page's geometry.
 *
 * Known approximation: the gap to keep between a unit and the next is the unit's own
 * `marginBottom`. For a list's last item the collapsed gap is really max(li 4, ol 8), which
 * only matters when landing − prevBottom falls in [4, 8): negligible.
 *
 * The plan is total, because a stored `y` or `setup` and the measurements can be anything a
 * crafted pattern or a broken layout engine hands over (a read-only view lays out too):
 * - If `y`, a margin, the page size or any unit edge isn't a finite number, nothing can be
 *   placed. The text stays on its own page, with no breaks, sized by the units whose bottom is
 *   finite, and `overflow` is set.
 * - A `marginBottom` that isn't finite counts as 0.
 * - The plan never reaches more than `MAX_FLOW_PAGES` pages. A break that would pass that stops
 *   all further breaks: the remaining units stay on the last page, and `overflow` is set. The
 *   work is bounded by units × `MAX_FLOW_PAGES`, whatever `y` is.
 */
export function planFlow(allUnits: readonly MeasuredUnit[], frame: FlowFrame, measurer?: FlowMeasurer): FlowPlan {
    const { y, setup, boxWidth } = frame;
    let units: readonly MeasuredUnit[] = allUnits;
    const pitch = pagePitch(setup);
    const pageHeight = pageDimensions(setup).height;
    const PAD = TEXT_BOX_PADDING;
    const readable =
        [y, setup.margins.top, setup.margins.bottom, pitch, pageHeight].every(Number.isFinite) &&
        units.every((unit) => Number.isFinite(unit.top) && Number.isFinite(unit.bottom));
    if (!readable) return unplacedPlan(units);

    // Page k >= 1 as its clamped override sets it. With no `boxWidth` the box's width is unknown: only
    // overrides count, and a width override differs from the (unknown) measured width.
    const pageOverride = (k: number): FlowOverride | null => {
        const stored = frame.overrides?.[k - 1];
        return stored ? clampFlowOverride(stored, setup, boxWidth ?? Number.NaN) : null;
    };
    const geometryOf = (k: number) => {
        const o = k === 0 ? null : pageOverride(k);
        return { dx: o?.dx ?? 0, dy: o?.dy ?? 0, width: o?.width ?? boxWidth, override: o };
    };
    // The width page k lays its text out at: its own, the box's, or NATURAL (the box's own, width unknown).
    const NATURAL = 'natural';
    const layoutWidth = (k: number): number | typeof NATURAL => geometryOf(k).width ?? NATURAL;
    // Page k's geometry for `classifySplit` and the measurer (NaN: the box's own, unknown, width).
    const numericWidth = (width: number | typeof NATURAL) => (width === NATURAL ? Number.NaN : width);
    const pageGeometry = (k: number): PageGeometry => ({ dx: geometryOf(k).dx, width: numericWidth(layoutWidth(k)) });

    let current = measurer; // measures the units from `base` on, at width `measuredWidth`
    let base = 0;
    let measuredWidth = layoutWidth(0);
    // Each measurer's lines, per unit (relative to it), asked for once.
    const linesCache = new Map<FlowMeasurer, Map<number, MeasuredLine[] | null>>();

    const landing = (k: number) => k * pitch + setup.margins.top - y + PAD + geometryOf(k).dy;
    const limit = (k: number) => k * pitch + pageHeight - setup.margins.bottom - y - PAD;
    const paper = (k: number) => k * pitch + pageHeight - y;
    const segmentTop = (k: number) => (k === 0 ? 0 : landing(k) - PAD);

    let page = 0;
    let shift = 0; // how far everything after the last break (or band) has been pushed down
    let first = 0; // the first unit on the current page
    let firstLine = 0; // the first line of unit `first` on the current page (> 0 when it continues a split)
    const overflowPages: OverflowPage[] = []; // one per page, ascending
    // A page already flagged keeps its entry; the cap's `tooTall` replaces an `edge` on the page it stops at.
    const flagOverflow = (k: number, reason: PaperErrorReason) => {
        const existing = overflowPages.find((o) => o.page === k);
        if (!existing) overflowPages.push({ page: k, reason });
        else if (reason === 'tooTall') existing.reason = 'tooTall';
    };
    const textAreaHeight = pageHeight - setup.margins.top - setup.margins.bottom;
    let capped = false; // a break would pass MAX_FLOW_PAGES: the rest of the text stays on the last page
    const pageOf: number[] = []; // the page each unit starts on
    const breaks: number[] = [];
    const offsets: Array<number | null> = [];
    const margins: Array<{ unit: number; px: number }> = [];
    const bands: FlowBand[] = [];
    const unitHeights: FlowPlan['unitHeights'] = [];
    // The unit being split (-1 for none) and its state: the geometry its tail is laid out at, its narrowing
    // (side and total inset from its box), and the heights of its bands the current measurements don't include.
    let splitting = -1;
    let splitGeometry: PageGeometry = { dx: 0, width: Number.NaN };
    let splitSide: 'left' | 'right' | null = null;
    let splitInset = 0;
    let pendingBands = 0;
    let fallbackPending = false; // a fallback split left the units after it measured at the head's geometry
    // Pages whose segment is the union of a fallback tail's geometry and the page's own.
    const unionSegments = new Map<number, { left: number; width: number }>();
    // Per page, the lowest edge of its segment (padding included). A page no unit lands on stays null.
    // The box is never shorter than its two paddings, so an empty one is 2 × PAD tall.
    const segmentBottoms: Array<number | null> = [2 * PAD];
    const reachPage = (k: number) => {
        while (segmentBottoms.length <= k) segmentBottoms.push(null);
    };
    const extendSegment = (k: number, bottom: number) => {
        segmentBottoms[k] = Math.max(segmentBottoms[k] ?? 0, bottom + PAD);
    };

    // The lines of unit i as the current measurer gives them, or null when it can't split.
    const linesOf = (i: number): MeasuredLine[] | null => {
        if (!current || units[i].splittable !== true || i < base) return null;
        let cache = linesCache.get(current);
        if (!cache) linesCache.set(current, (cache = new Map()));
        if (!cache.has(i - base)) {
            const lines = current.lines(i - base);
            cache.set(i - base, usableLines(lines) ? lines : null);
        }
        return cache.get(i - base) ?? null;
    };

    // Unit i measured again with `band` (narrowing) live, its tail at `tail`; null when the result is unusable.
    // `cutOffset` is the char offset of the line the band pushes down: its index in the new lines is returned.
    const narrow = (i: number, band: FlowBand, tail: PageGeometry, cutOffset: number) => {
        if (!current) return null;
        const unitBands = [...bands.filter((b) => b.unit === i), band];
        const again = current.remeasureSplit(i - base, unitBands, tail);
        if (again.units.length !== units.length - i || !again.units.every(isReadable)) return null;
        const cache = new Map<number, MeasuredLine[] | null>();
        linesCache.set(again.measurer, cache);
        const lines = again.measurer.lines(0);
        if (!usableLines(lines)) return null;
        cache.set(0, lines);
        const from = lines.findIndex((line) => line.offset === cutOffset);
        if (from < 1) return null;
        return { again, from };
    };

    /**
     * Splits unit i at the current page's break when the orphan/widow rule (ruling 1) and the pages' geometry
     * allow it: of its lines from `from` on (those on this page), the ones that fit stay, and a band pushes the
     * rest to the next page. `isFirst` is whether unit i is the first on this page. Returns whether it split.
     */
    const trySplit = (i: number, isFirst: boolean, from: number): boolean => {
        if (capped) return false;
        const lines = linesOf(i);
        if (!lines || from >= lines.length) return false;
        const n = lines.length - from;
        let fit = 0;
        while (fit < n && lines[from + fit].bottom + shift <= limit(page)) fit++;
        let keep: number;
        if (isFirst) {
            // fit 0: stays and overflows, as today; all lines fit (only its bottom padding doesn't): no split
            if (fit < 1 || fit === n) return false;
            keep = Math.max(1, Math.min(fit, n - MIN_SPLIT_LINES));
        } else {
            keep = Math.min(fit, n - MIN_SPLIT_LINES);
            if (keep < MIN_SPLIT_LINES) return false; // the whole unit moves
        }
        if (keep >= n) return false;
        const cut = lines[from + keep];
        const next = page + 1;
        // A split lands on the next page; one whose cut would pass that page's landing (skipping it) doesn't split.
        if (next >= MAX_FLOW_PAGES || landing(next) < cut.top + shift) return false;

        // The unit's split state so far, or a fresh one at this page (committed only once it splits).
        const fresh = splitting !== i;
        const head: PageGeometry = fresh ? { dx: geometryOf(page).dx, width: numericWidth(measuredWidth) } : splitGeometry;
        const side = fresh ? null : splitSide;
        const inset = fresh ? 0 : splitInset;
        const pending = fresh ? 0 : pendingBands;
        const tail = pageGeometry(next);
        let kind = classifySplit(head, tail);
        if (typeof kind === 'object' && side !== null && kind.side !== side) kind = 'other';
        if (kind === 'other' && !isFirst) return false;
        const gap = landing(next) - (cut.top + shift);
        const top = cut.top - units[i].top + pending;
        let narrowed: ReturnType<typeof narrow> = null;
        if (typeof kind === 'object') {
            const band: FlowBand = { unit: i, top, height: gap, side: kind.side, inset: inset + kind.inset };
            narrowed = narrow(i, band, tail, cut.offset);
            if (!narrowed && !isFirst) return false; // an unusable re-measure: treated as `other`
        }

        splitting = i;
        splitGeometry = head;
        splitSide = side;
        splitInset = inset;
        pendingBands = pending;
        extendSegment(page, lines[from + keep - 1].bottom + shift);
        breaks.push(i);
        offsets.push(cut.offset);
        if (narrowed && typeof kind === 'object') {
            splitSide = kind.side;
            splitInset += kind.inset;
            bands.push({ unit: i, top, height: gap, side: splitSide, inset: splitInset });
            units = [...units.slice(0, i), ...narrowed.again.units];
            current = narrowed.again.measurer;
            base = i;
            measuredWidth = layoutWidth(next);
            splitGeometry = tail;
            // The new positions include all of the unit's bands: the shift is the one before them.
            shift -= pendingBands;
            pendingBands = 0;
            firstLine = narrowed.from;
        } else {
            // plain, or a fallback: the tail keeps the unit's geometry (and its narrowing, if any)
            bands.push({ unit: i, top, height: gap, side: splitSide, inset: splitInset });
            shift += gap;
            pendingBands += gap;
            firstLine = from + keep;
            if (kind !== 'plain') {
                fallbackPending = true;
                const h = splitGeometry;
                if (Number.isFinite(h.width) && Number.isFinite(tail.width)) {
                    const left = Math.min(h.dx, tail.dx);
                    unionSegments.set(next, { left, width: Math.max(h.dx + h.width, tail.dx + tail.width) - left });
                }
            }
        }
        first = i;
        page = next;
        reachPage(page);
        return true;
    };

    for (let i = 0; i < units.length; i++) {
        const startPage = page;
        let split = false;
        if (!capped && i > first && units[i].bottom + shift > limit(page) && !(split = trySplit(i, false, 0))) {
            const prev = units[i - 1];
            const prevBottom = prev.bottom + shift;
            const gap = Number.isFinite(prev.marginBottom) ? prev.marginBottom : 0;
            let next = page + 1;
            while (next < MAX_FLOW_PAGES && landing(next) - prevBottom < gap) next++; // skip the pages `prev` covers
            if (next >= MAX_FLOW_PAGES) {
                capped = true;
                flagOverflow(page, 'tooTall');
            } else {
                for (let k = page + 1; k <= next; k++) {
                    breaks.push(i); // covered pages get empty slices
                    offsets.push(null);
                }
                margins.push({ unit: i, px: landing(next) - prevBottom });
                first = i;
                firstLine = 0;
                page = next;
                // A page of another width lays the text out again from this unit on.
                const width = layoutWidth(page);
                if (current && width !== measuredWidth) {
                    const again = current.remeasure(i - base, pageGeometry(page));
                    if (again.units.length === units.length - i && again.units.every(isReadable)) {
                        units = [...units.slice(0, i), ...again.units];
                        current = again.measurer;
                        base = i;
                        measuredWidth = width;
                    }
                }
                shift = landing(page) - units[i].top;
                reachPage(page);
            }
        }
        pageOf.push(split ? startPage : page); // the page the unit starts on
        // A unit that starts its page (or continues a split) and is still too tall splits again, page after
        // page, while it can.
        while (i === first && units[i].bottom + shift > limit(page) && trySplit(i, true, firstLine)) {
            // trySplit moved on to the next page
        }
        const unit = units[i]; // possibly measured again at the new page's width
        extendSegment(page, unit.bottom + shift);
        if (i === first && unit.bottom + shift > paper(page)) {
            flagOverflow(page, unit.bottom - unit.top > textAreaHeight ? 'tooTall' : 'edge');
        }
        if (bands[bands.length - 1]?.unit === i) unitHeights.push({ unit: i, height: unit.bottom - unit.top + pendingBands });
        // After a fallback split the units after this one go back to their page's geometry.
        if (fallbackPending) {
            fallbackPending = false;
            const width = layoutWidth(page);
            if (current && i + 1 < units.length && width !== measuredWidth) {
                const again = current.remeasure(i + 1 - base, pageGeometry(page));
                if (again.units.length === units.length - i - 1 && again.units.every(isReadable)) {
                    units = [...units.slice(0, i + 1), ...again.units];
                    current = again.measurer;
                    base = i + 1;
                    measuredWidth = width;
                }
            }
        }
    }

    const segments = segmentBottoms.map((bottom, k) =>
        bottom === null
            ? null
            : {
                  top: segmentTop(k),
                  height: bottom - segmentTop(k),
                  left: unionSegments.get(k)?.left ?? geometryOf(k).dx,
                  width: unionSegments.get(k)?.width ?? geometryOf(k).override?.width ?? null,
              },
    );
    // A later page differs from the first when it is moved or has a width of its own. With an unknown box
    // width, a page without a width of its own has no width to report, so it gets no entry.
    const geometry: FlowPlan['geometry'] = [];
    pageOf.forEach((k, unit) => {
        const { dx, override } = geometryOf(k);
        const width = override?.width ?? boxWidth;
        if (k > 0 && (dx !== 0 || override?.width !== undefined) && width !== undefined) {
            geometry.push({ unit, dx, width });
        }
    });
    // Page 0 always holds the first unit (or is the empty box), so it is never null.
    return {
        breaks,
        margins,
        segments,
        height: segments[0]?.height ?? 2 * PAD,
        overflow: overflowPages.length > 0,
        geometry,
        offsets,
        bands,
        unitHeights,
        overflowPages,
    };
}

const isReadable = (unit: MeasuredUnit) => Number.isFinite(unit.top) && Number.isFinite(unit.bottom);

/** The plan for text that can't be placed (see `planFlow`): one page, no breaks, flagged as overflowing,
 *  as tall as its lowest readable unit. */
function unplacedPlan(units: readonly MeasuredUnit[]): FlowPlan {
    const height = units.reduce(
        (lowest, unit) => (Number.isFinite(unit.bottom) ? Math.max(lowest, unit.bottom + TEXT_BOX_PADDING) : lowest),
        2 * TEXT_BOX_PADDING,
    );
    return {
        breaks: [],
        margins: [],
        segments: [{ top: 0, height, left: 0, width: null }],
        height,
        overflow: true,
        geometry: [],
        offsets: [],
        bands: [],
        unitHeights: [],
        overflowPages: [{ page: 0, reason: 'tooTall' }],
    };
}

/** Whether `b` lays the text out like `a`. The margins aren't compared: a plan whose breaks, offsets, bands,
 *  segments, height and overflow match needs no new write to the document, even if a margin moved a pixel. */
export function sameFlowPlan(a: FlowPlan | null, b: FlowPlan): boolean {
    if (a === null) return false;
    return (
        a.height === b.height &&
        a.overflow === b.overflow &&
        a.overflowPages.length === b.overflowPages.length &&
        a.overflowPages.every((o, i) => o.page === b.overflowPages[i].page && o.reason === b.overflowPages[i].reason) &&
        a.breaks.length === b.breaks.length &&
        a.breaks.every((value, i) => value === b.breaks[i]) &&
        a.segments.length === b.segments.length &&
        a.segments.every((segment, i) => {
            const other = b.segments[i];
            if (segment === null || other === null) return segment === other;
            return (
                segment.top === other.top &&
                segment.height === other.height &&
                segment.left === other.left &&
                segment.width === other.width
            );
        }) &&
        a.geometry.length === b.geometry.length &&
        a.geometry.every((g, i) => g.unit === b.geometry[i].unit && g.dx === b.geometry[i].dx && g.width === b.geometry[i].width) &&
        a.offsets.length === b.offsets.length &&
        a.offsets.every((offset, i) => offset === b.offsets[i]) &&
        a.bands.length === b.bands.length &&
        a.bands.every((band, i) => {
            const other = b.bands[i];
            return (
                band.unit === other.unit &&
                band.top === other.top &&
                band.height === other.height &&
                band.side === other.side &&
                band.inset === other.inset
            );
        }) &&
        a.unitHeights.length === b.unitHeights.length &&
        a.unitHeights.every((h, i) => h.unit === b.unitHeights[i].unit && h.height === b.unitHeights[i].height)
    );
}
